import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  ArrowLeft, Printer, Plus, Trash2, Save, FileText, Calendar,
  AlertTriangle, CheckCircle2, Send, Edit3, Paperclip, X, ExternalLink, Download,
} from "lucide-react";
import { getToken } from "@/lib/session";

export default function AdminInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/invoices/${id}`).then((r) => {
    setInv(r.data);
    setForm({
      title: r.data.title || "",
      due_date: r.data.due_date || "",
      tax_rate: r.data.tax_rate ?? 0,
      discount: r.data.discount ?? 0,
      status: r.data.status || "open",
      notes: r.data.notes || "",
      notes_client: r.data.notes_client || "",
      line_items: (r.data.line_items && r.data.line_items.length > 0) ? r.data.line_items : [{ description: "", qty: 1, unit_price: 0 }],
    });
  }).catch(() => toast.error("Could not load invoice"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const subtotal = (form.line_items || []).reduce((a, li) => a + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0);
  const afterDisc = Math.max(0, subtotal - (parseFloat(form.discount) || 0));
  const taxAmt = afterDisc * (parseFloat(form.tax_rate) || 0);
  const grand = afterDisc + taxAmt;

  const setLi = (i, key, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => idx === i ? { ...li, [key]: v } : li) }));
  const addLi = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", qty: 1, unit_price: 0 }] }));
  const delLi = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/invoices/${id}`, {
        ...form,
        discount: parseFloat(form.discount) || 0,
        tax_rate: parseFloat(form.tax_rate) || 0,
        line_items: form.line_items.filter((li) => li.description?.trim()),
      });
      toast.success("Invoice saved");
      setEdit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendToClient = async () => {
    if (!window.confirm("Send this invoice to the client for review?")) return;
    try {
      await api.post(`/invoices/${id}/send`);
      toast.success("Invoice sent — client has been notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const resendRevised = async () => {
    if (!window.confirm("Send the revised version back to the client?")) return;
    try {
      await api.post(`/invoices/${id}/resend`);
      toast.success("Revised invoice sent — client has been notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const attachPdf = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/invoices/${id}/attach-pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF attached");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  if (!inv) return <div className="text-[#475569]">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto" data-testid="invoice-detail">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={() => navigate(-1)} className="text-sm text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white inline-flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex flex-wrap gap-2">
          {(inv.status === "draft") && (
            <button onClick={sendToClient} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="send-btn">
              <Send className="w-4 h-4" /> Send to client
            </button>
          )}
          {inv.status === "revision_requested" && (
            <button onClick={resendRevised} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="resend-btn">
              <Send className="w-4 h-4" /> Send revised version
            </button>
          )}
          <label className="btn-outline-navy text-sm py-2 px-3 inline-flex items-center gap-1 cursor-pointer" data-testid="attach-pdf-label">
            <Paperclip className="w-4 h-4" />
            {inv.pdf_file_id ? "Replace PDF" : "Attach PDF"}
            <input
              type="file" accept="application/pdf,image/*"
              onChange={(e) => attachPdf(e.target.files?.[0])}
              className="hidden"
              data-testid="attach-pdf-input"
            />
          </label>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.invoice_id}/pdf?auth=${encodeURIComponent(getToken() || "")}`}
            target="_blank" rel="noreferrer"
            className="btn-outline-navy text-sm py-2 px-3 inline-flex items-center gap-1"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" /> PDF
          </a>
          <button onClick={() => window.print()} className="btn-outline-navy text-sm py-2 px-3" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </button>
          {!edit && (
            <button onClick={() => setEdit(true)} className="btn-accent text-sm" data-testid="edit-btn">
              Edit invoice
            </button>
          )}
        </div>
      </div>

      {/* Revision-requested banner */}
      {inv.status === "revision_requested" && inv.current_revision_note && (
        <div
          className="mb-6 rounded-xl border border-sky-300 dark:border-sky-800/50 bg-sky-50/80 dark:bg-sky-900/10 p-4 sm:p-5 print:hidden"
          data-testid="revision-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-500 grid place-items-center text-white shrink-0">
              <Edit3 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg font-medium text-[#0A2540] dark:text-white">
                Client requested a revision
              </h3>
              <p className="mt-2 text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">
                {inv.current_revision_note}
              </p>
              <p className="mt-2 text-[11px] text-[#475569] dark:text-slate-400">
                Edit the invoice below, then click <strong>Send revised version</strong> above.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Denied banner */}
      {inv.status === "denied" && (
        <div className="mb-6 rounded-xl border border-red-300 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 p-4 sm:p-5 print:hidden">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500 grid place-items-center text-white shrink-0">
              <X className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg font-medium text-[#0A2540] dark:text-white">
                Client declined this invoice
              </h3>
              {inv.denial_note && (
                <p className="mt-2 text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">
                  {inv.denial_note}
                </p>
              )}
              <button onClick={resendRevised} className="mt-3 btn-accent text-sm inline-flex items-center gap-1" data-testid="resend-after-deny">
                <Send className="w-4 h-4" /> Send a new version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attached PDF preview */}
      {inv.pdf_file_id && (
        <div className="mb-6 rounded-xl border border-[#E2E8F0] dark:border-slate-700 overflow-hidden print:hidden" data-testid="attached-pdf">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900/60 text-xs">
            <span className="inline-flex items-center gap-1.5 font-medium text-[#0A2540] dark:text-white">
              <FileText className="w-3.5 h-3.5" /> {inv.pdf_filename || "invoice.pdf"}
            </span>
            <a
              href={`/api/files/${inv.pdf_file_id}${getToken() ? `?auth=${encodeURIComponent(getToken())}` : ""}`}
              target="_blank" rel="noreferrer"
              data-testid="open-attached-pdf"
              className="inline-flex items-center gap-1 text-[#C9A356] hover:text-[#DDBD77]"
            >
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <object
            data={`/api/files/${inv.pdf_file_id}${getToken() ? `?auth=${encodeURIComponent(getToken())}` : ""}`}
            type="application/pdf"
            className="w-full h-[420px] bg-slate-100 dark:bg-slate-900"
          >
            <div className="p-4 text-xs text-[#475569] dark:text-slate-400">
              Preview unavailable — use the "Open" link above.
            </div>
          </object>
        </div>
      )}

      <div className="card-pb p-8 print:shadow-none print:border-0 printable-doc">
        {/* Print-only PowerBuilt letterhead */}
        <div className="doc-brand-band">
          <div className="brand-eyebrow">PowerBuilt SXM · Solar · Electrical · Maintenance</div>
          <div className="brand-title">Invoice</div>
          <div className="brand-sub">Issued {new Date(inv.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</div>
          <div className="brand-meta">
            {inv.invoice_id}
            {inv.due_date ? ` · Due ${new Date(inv.due_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : ""}
          </div>
        </div>

        {/* Status stamp: overlays the paper corner on print */}
        {inv.status === "paid" && <div className="doc-stamp paid">Paid</div>}
        {inv.is_overdue && inv.status !== "paid" && <div className="doc-stamp overdue">Overdue</div>}
        {inv.status === "draft" && <div className="doc-stamp draft">Draft</div>}
        {inv.status === "declined" && <div className="doc-stamp declined">Declined</div>}

        {/* Screen header (hidden in print) */}
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8 pb-6 border-b border-[#E2E8F0] dark:border-slate-700 print:hidden">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="" className="w-12 h-12 object-contain" />
              <div>
                <div className="font-heading font-bold text-[#0A2540] dark:text-white">POWERBUILT</div>
                <div className="text-[10px] tracking-wider text-[#475569] dark:text-slate-400">SOLAR · ELECTRICAL · MAINTENANCE</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400">Invoice</div>
            <div className="font-mono text-lg text-[#0A2540] dark:text-white">{inv.invoice_id}</div>
            <StatusBadge inv={inv} />
          </div>
        </div>

        {/* From / To */}
        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <div>
            <div className="overline mb-2">Bill from</div>
            <div className="text-sm text-[#0A2540] dark:text-white font-medium">PowerBuilt</div>
            <div className="text-xs text-[#475569] dark:text-slate-400">Cole Bay, Sint Maarten</div>
            <div className="text-xs text-[#475569] dark:text-slate-400">hello@powerbuilt.sxm</div>
          </div>
          <div>
            <div className="overline mb-2">Bill to</div>
            <div className="text-sm text-[#0A2540] dark:text-white font-medium">{inv.client?.name || "—"}</div>
            <div className="text-xs text-[#475569] dark:text-slate-400 font-mono">{inv.client?.customer_code}</div>
            <div className="text-xs text-[#475569] dark:text-slate-400">{inv.client?.email}</div>
            {inv.client?.phone && <div className="text-xs text-[#475569] dark:text-slate-400">{inv.client.phone}</div>}
            {inv.client?.address && <div className="text-xs text-[#475569] dark:text-slate-400 mt-1">{inv.client.address}</div>}
          </div>
        </div>

        {/* Title / dates */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="sm:col-span-3">
            <div className="overline mb-1">Title</div>
            {edit ? (
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2" />
            ) : (
              <div className="font-medium text-[#0A2540] dark:text-white">{inv.title}</div>
            )}
          </div>
          <div>
            <div className="overline mb-1">Issued</div>
            <div className="text-sm">{new Date(inv.created_at).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="overline mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Due</div>
            {edit ? (
              <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="edit-due"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            ) : (
              <div className={`text-sm ${inv.is_overdue ? "text-red-600 font-bold" : "text-[#0A2540] dark:text-white"}`}>
                {inv.is_overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}
              </div>
            )}
          </div>
          {inv.linked_project && (
            <div>
              <div className="overline mb-1">Linked project</div>
              <div className="text-sm">{inv.linked_project.title}</div>
            </div>
          )}
        </div>

        {/* Line items */}
        <table className="w-full mb-4">
          <thead>
            <tr className="border-b border-[#E2E8F0] dark:border-slate-700 text-left text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400">
              <th className="pb-2 font-bold">Description</th>
              <th className="pb-2 font-bold text-right w-20">Qty</th>
              <th className="pb-2 font-bold text-right w-28">Unit price</th>
              <th className="pb-2 font-bold text-right w-28">Subtotal</th>
              {edit && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {(edit ? form.line_items : (inv.line_items?.length ? inv.line_items : [{ description: inv.title, qty: 1, unit_price: inv.amount }])).map((li, i) => {
              const sub = (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0);
              const rowKey = li._id || `${li.description || "row"}-${i}`;
              return (
                <tr key={rowKey} className="border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`line-${i}`}>
                  <td className="py-2 pr-3">
                    {edit ? <input value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} placeholder="Service / item"
                                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1 text-sm"
                                   data-testid={`li-desc-${i}`} /> : <span className="text-sm">{li.description}</span>}
                  </td>
                  <td className="py-2 px-1 text-right">
                    {edit ? <input type="number" step="0.01" value={li.qty} onChange={(e) => setLi(i, "qty", e.target.value)}
                                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1 text-sm text-right"
                                   data-testid={`li-qty-${i}`} /> : <span className="text-sm">{li.qty}</span>}
                  </td>
                  <td className="py-2 px-1 text-right">
                    {edit ? <input type="number" step="0.01" value={li.unit_price} onChange={(e) => setLi(i, "unit_price", e.target.value)}
                                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1 text-sm text-right"
                                   data-testid={`li-price-${i}`} /> : <span className="text-sm">${parseFloat(li.unit_price).toFixed(2)}</span>}
                  </td>
                  <td className="py-2 pl-3 text-right text-sm tabular-nums">${sub.toFixed(2)}</td>
                  {edit && (
                    <td className="text-right">
                      <button onClick={() => delLi(i)} className="text-red-500 hover:text-red-600" data-testid={`del-li-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {edit && (
          <button onClick={addLi} className="text-xs text-[#C9A356] hover:text-[#DDBD77] inline-flex items-center gap-1 mb-4" data-testid="add-line">
            <Plus className="w-3 h-3" /> Add line item
          </button>
        )}

        {/* Totals */}
        <div className="flex justify-end mb-6 print:mt-6">
          <div className="w-72 space-y-1.5 text-sm">
            <Row label="Subtotal" value={`$${(edit ? subtotal : (inv.subtotal ?? inv.amount ?? 0)).toFixed(2)}`} />
            {edit ? (
              <Row label="Discount" value={
                <input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })}
                       data-testid="edit-discount"
                       className="w-24 border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-0.5 text-sm text-right" />
              } />
            ) : (
              (inv.discount > 0) && <Row label="Discount" value={`–$${(inv.discount).toFixed(2)}`} />
            )}
            {edit ? (
              <Row label="Tax rate" value={
                <select value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) })}
                        data-testid="edit-tax-rate"
                        className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-0.5 text-sm">
                  <option value="0">No tax</option>
                  <option value="0.05">5% (SXM TOT)</option>
                  <option value="0.0625">6.25%</option>
                  <option value="0.10">10%</option>
                </select>
              } />
            ) : (
              ((inv.tax_amount > 0) && <Row label={`Tax (${((inv.tax_rate || 0) * 100).toFixed(2)}%)`} value={`$${(inv.tax_amount).toFixed(2)}`} />)
            )}
            <div className="flex justify-between items-center pt-2 border-t border-[#E2E8F0] dark:border-slate-700 text-base font-bold text-[#0A2540] dark:text-white">
              <span>Grand total</span>
              <span className="tabular-nums" data-testid="grand-total">${(edit ? grand : (inv.grand_total ?? inv.amount ?? 0)).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {(inv.notes_client || edit) && (
          <div className="mb-4">
            <div className="overline mb-1">Notes (visible to client)</div>
            {edit ? (
              <textarea value={form.notes_client} onChange={(e) => setForm({ ...form, notes_client: e.target.value })} rows={2} data-testid="edit-notes-client"
                        className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            ) : (
              <p className="text-sm text-[#475569] dark:text-slate-300 whitespace-pre-wrap">{inv.notes_client}</p>
            )}
          </div>
        )}
        {(inv.notes || edit) && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/10 border-l-2 border-amber-500 rounded print:hidden">
            <div className="overline text-amber-700 dark:text-amber-400 mb-1">Internal notes (admin-only)</div>
            {edit ? (
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="edit-notes"
                        className="w-full border border-amber-200 dark:border-amber-700/50 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            ) : (
              <p className="text-sm text-amber-800 dark:text-amber-300 whitespace-pre-wrap">{inv.notes}</p>
            )}
          </div>
        )}

        {/* Save bar */}
        {edit && (
          <div className="flex gap-2 justify-end pt-4 border-t border-[#E2E8F0] dark:border-slate-700">
            <button onClick={() => { setEdit(false); load(); }} className="btn-outline-navy text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-invoice">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      {/* Payment history */}
      <div className="card-pb p-6 mt-6 print:hidden" data-testid="payment-history">
        <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#C9A356]" /> Payment activity
        </h3>
        {inv.status === "paid" ? (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50 rounded-md p-3 mb-3">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-medium text-sm">
              <CheckCircle2 className="w-4 h-4" /> Paid {inv.paid_at && `· ${new Date(inv.paid_at).toLocaleString()}`}
            </div>
            {inv.paid_method && <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">Method: {inv.paid_method.replace(/_/g, " ")}{inv.paid_notes && ` · ${inv.paid_notes}`}</div>}
          </div>
        ) : (
          <p className="text-sm text-[#475569] dark:text-slate-400 mb-3">Awaiting payment.</p>
        )}
        {(inv.payment_attempts || []).length === 0 ? (
          <p className="text-xs text-[#94A3B8] italic">No Stripe attempts yet.</p>
        ) : (
          <ul className="space-y-2">
            {inv.payment_attempts.map((tx) => (
              <li key={tx.transaction_id} className="border border-[#E2E8F0] dark:border-slate-700 rounded-md p-3 text-xs">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-[#475569] dark:text-slate-400">{tx.transaction_id}</div>
                    <div className="text-[#0A2540] dark:text-white">${(tx.amount || 0).toFixed(2)} · {tx.payment_status}</div>
                  </div>
                  <div className="text-[#94A3B8]">{new Date(tx.created_at).toLocaleString()}</div>
                </div>
                {tx.metadata?.method && <div className="text-[10px] text-[#475569] mt-1">Method: {tx.metadata.method}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ inv }) {
  const labelOverride = inv.is_overdue && inv.status !== "paid" ? "overdue" : inv.status;
  const styleMap = {
    paid: "bg-emerald-600 text-white",
    overdue: "bg-red-100 text-red-700",
    void: "bg-slate-200 text-slate-600",
    draft: "bg-slate-100 text-slate-600",
    sent: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    revision_requested: "bg-sky-100 text-sky-700",
    denied: "bg-red-100 text-red-700",
  };
  const labels = {
    revision_requested: "Revision requested",
  };
  const cls = styleMap[labelOverride] || "bg-amber-100 text-amber-700";
  return (
    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full mt-1 inline-block ${cls}`}>
      {labels[labelOverride] || labelOverride}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#475569] dark:text-slate-400">{label}</span>
      <span className="text-[#0A2540] dark:text-white tabular-nums">{value}</span>
    </div>
  );
}
